export type FacingMode = 'user' | 'environment'

export interface CameraSession {
  stream: MediaStream
  facing: FacingMode
}

export async function startCamera(video: HTMLVideoElement, facing: FacingMode = 'user'): Promise<CameraSession> {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error('Camera access is not available in this browser.')
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: facing },
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 }
    },
    audio: false
  })

  video.autoplay = true
  video.muted = true
  video.playsInline = true
  video.srcObject = stream
  try {
    await video.play()
    await waitForVideo(video)
    return { stream, facing }
  } catch (error) {
    stopCamera({ stream, facing })
    video.srcObject = null
    throw new Error(`The camera opened but could not start: ${messageFrom(error)}`)
  }
}

export function stopCamera(session: CameraSession | null): void {
  session?.stream.getTracks().forEach((track) => track.stop())
}

export function friendlyCameraError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : ''
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Camera permission stayed off. Allow it in browser settings or play without camera.'
  if (name === 'NotFoundError') return 'No camera was found. Pointer, touch, and keyboard remain available.'
  if (name === 'NotReadableError') return 'Another app may be using the camera. Close it and try again.'
  return error instanceof Error ? error.message : 'The camera could not start. You can still play without it.'
}

function waitForVideo(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => finish(new Error('No usable camera frame arrived.')), 5_000)
    const onReady = (): void => {
      if (video.videoWidth > 0) finish()
    }
    const finish = (error?: Error): void => {
      window.clearTimeout(timeout)
      video.removeEventListener('loadeddata', onReady)
      video.removeEventListener('canplay', onReady)
      if (error) reject(error)
      else resolve()
    }
    video.addEventListener('loadeddata', onReady)
    video.addEventListener('canplay', onReady)
  })
}

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown playback error'
}
